"use client"
import type React from "react"
import { useEffect, useState } from "react"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
  FormFeedback,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Badge,
} from "reactstrap"
import Swal from "sweetalert2"
import { toast } from "react-toastify"
import { FaLayerGroup, FaBuilding, FaHashtag, FaToggleOn } from "react-icons/fa"
import {
  ExhibitorRelatedService,
  type Exhibition,
  type ExhibitionStandCategory,
} from "../../../Service/exhibitorsRelatedService"
import { Participation } from "./types"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: Participation | null
  exhibitorId: number
}

const ParticipationModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, initialData, exhibitorId }) => {
  // form state (status is now boolean)
  const [form, setForm] = useState<Omit<Participation, "id">>({
    exhibition: 0,
    exhibitor: exhibitorId,
    stand_category: 0,
    num_of_stands: 1,
    status: true,
  })

  // dropdown data
  const [exhibitions, setExhibitions] = useState<Exhibition[]>([])
  const [categories, setCategories] = useState<ExhibitionStandCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // load exhibitions
  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      setLoadingData(true)
      try {
        const exs = await ExhibitorRelatedService.getExhibitions()
        setExhibitions(exs)
      } catch (err) {
        console.error(err)
        toast.error("Failed to load exhibitions")
      } finally {
        setLoadingData(false)
      }
    })()
  }, [isOpen])

  // load categories when exhibition changes
  useEffect(() => {
    if (!form.exhibition) return setCategories([])
    ;(async () => {
      setLoadingData(true)
      try {
        const cats = await ExhibitorRelatedService.getStandCategories(form.exhibition)
        setCategories(cats)
      } catch (err) {
        console.error(err)
        toast.error("Failed to load categories")
      } finally {
        setLoadingData(false)
      }
    })()
  }, [form.exhibition])

  // initialize form
  useEffect(() => {
    if (initialData) {
      setForm({
        exhibition: initialData.exhibition,
        exhibitor: exhibitorId,
        stand_category: initialData.stand_category,
        num_of_stands: initialData.num_of_stands || 1,
        status: initialData.status,
      })
    } else {
      setForm({
        exhibition: 0,
        exhibitor: exhibitorId,
        stand_category: 0,
        num_of_stands: 1,
        status: true,
      })
    }
    setErrors({})
  }, [initialData, exhibitorId, isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target

    setForm({
      ...form,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    })

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }))
  }

  const validateForm = () => {
    const newErr: { [k: string]: string } = {}
    if (!form.exhibition) newErr.exhibition = "Exhibition is required"
    if (!form.stand_category) newErr.stand_category = "Stand category is required"
    if (!form.num_of_stands || Number(form.num_of_stands) <= 0)
      newErr.num_of_stands = "Number of stands must be greater than 0"
    return newErr
  }

  const handleSubmit = async () => {
    const valErr = validateForm()
    if (Object.keys(valErr).length) {
      setErrors(valErr)
      return
    }

    setLoading(true)
    try {
      if (initialData) {
        await ExhibitorRelatedService.updateParticipation(initialData.id, form)
        await Swal.fire("Updated!", "Participation updated.", "success")
      } else {
        await ExhibitorRelatedService.createParticipation(form)
        await Swal.fire("Created!", "Participation created.", "success")
      }
      onSuccess()
      toggle()
    } catch (err) {
      console.error(err)
      await Swal.fire("Error", "Something went wrong.", "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="modal-dialog-centered">
      <ModalHeader toggle={toggle} className="bg-primary text-white">
        <FaLayerGroup className="me-2" />
        {initialData ? "Edit Participation" : "Add Participation"}
      </ModalHeader>
      <ModalBody className="p-4">
        <Card className="mb-3 shadow-sm border-0">
          <CardHeader className="bg-light">
            <CardTitle tag="h6" className="mb-0 text-primary d-flex align-items-center">
              <FaBuilding className="me-2" />
              Exhibition Details
            </CardTitle>
            <small className="text-muted">Choose which exhibition and stand category this applies to</small>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label className="fw-bold">
                    Exhibition <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    name="exhibition"
                    value={form.exhibition}
                    onChange={handleChange}
                    invalid={!!errors.exhibition}
                    disabled={loadingData}
                    className="form-control-lg"
                  >
                    <option value={0}>-- Select Exhibition --</option>
                    {exhibitions.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.title}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{errors.exhibition}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label className="fw-bold">
                    Stand Category <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    name="stand_category"
                    value={form.stand_category}
                    onChange={handleChange}
                    invalid={!!errors.stand_category}
                    disabled={loadingData || !form.exhibition}
                    className="form-control-lg"
                  >
                    <option value={0}>-- Select Category --</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.category}>
                        {c.stand_name}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{errors.stand_category}</FormFeedback>
                  {!form.exhibition && (
                    <small className="text-muted">Select an exhibition first</small>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="shadow-sm border-0">
          <CardHeader className="bg-light">
            <CardTitle tag="h6" className="mb-0 text-primary d-flex align-items-center">
              <FaHashtag className="me-2" />
              Stand & Status
            </CardTitle>
          </CardHeader>
          <CardBody>
            <Row className="align-items-center">
              <Col md="6">
                <FormGroup className="mb-md-0">
                  <Label className="fw-bold">
                    Number of Stands <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="number"
                    name="num_of_stands"
                    value={form.num_of_stands}
                    onChange={handleChange}
                    invalid={!!errors.num_of_stands}
                    min="1"
                    placeholder="Enter number of stands"
                    className="form-control-lg"
                  />
                  <FormFeedback>{errors.num_of_stands}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md="6">
                <Label className="fw-bold d-flex align-items-center">
                  <FaToggleOn className="me-2" />
                  Status
                </Label>
                <FormGroup check className="form-switch d-flex align-items-center gap-2 mb-0">
                  <Input
                    type="checkbox"
                    name="status"
                    checked={form.status}
                    onChange={handleChange}
                    className="form-check-input"
                    style={{ width: "2.5em", height: "1.4em" }}
                  />
                  <Badge pill color={form.status ? "success" : "secondary"} className="px-3 py-2">
                    {form.status ? "Active" : "Inactive"}
                  </Badge>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </ModalBody>

      <ModalFooter className="bg-light">
        <Button color="secondary" onClick={toggle} disabled={loading} className="me-2">
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={loading || loadingData}>
          {loading && <Spinner size="sm" className="me-2" />}
          {initialData ? "Update Participation" : "Create Participation"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ParticipationModal